import React, { useCallback, useContext, useMemo } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation, Trans } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react-lite";
import styles from "./AttendanceList.module.css";
import { IAssemblyAttendances } from "src/stores/assemblies/AttendanceListStore";
import { ScreenSize, UI } from "@wwimmo/ui";
import { SCREEN_SIZE } from "src/stores/UIStore";
import { AttendanceListRowDesktop } from "./AttendanceListRowDesktop";
import { AttendanceListRowMobile } from "./AttendanceListRowMobile";
import AttendanceQuorumInfo from "src/screens/assemblies/assembly/attendance/AttendanceQuorumInfo";

interface IAttendanceListProps {
    assemblyid: string;
    isLiveModeSmallView: boolean;
}

const AttendanceDesktopTabsBase = ({
    assemblyid,
    isLiveModeSmallView
}: {
    assemblyid: string;
    isLiveModeSmallView: boolean;
}) => {
    const { attendanceListStore, uiStore, assemblyStore } = useContext(RootStoreContext);

    const onTabsChange = useCallback(
        (realestatId: string) => {
            if (realestatId !== attendanceListStore.selectedRealestateId) {
                attendanceListStore.setSelectedRealestateId(realestatId);
                if (assemblyid && realestatId) {
                    attendanceListStore.loadAttendanceByRealestateId(assemblyid, realestatId);
                }
            }
        },
        [assemblyid, attendanceListStore]
    );

    if (uiStore.isMobile || isLiveModeSmallView) {
        return null;
    }

    return (
        <div className={styles.Tabbar}>
            <UI.Tabs
                selectedId={attendanceListStore.selectedRealestateId}
                onTabChange={onTabsChange}
                contents={
                    assemblyStore.currentAssembly
                        ? assemblyStore.currentAssembly.assemblyrealestates.map((item) => ({
                              label: item.realestate.name,
                              id: item.realestate.id
                          }))
                        : []
                }
                nowrap
            ></UI.Tabs>
        </div>
    );
};
const AttendanceDesktopTabs = observer(AttendanceDesktopTabsBase);

const AttendanceListRendererBase = (props: IAttendanceListProps) => {
    const { t } = useTranslation();
    const { attendanceListStore, uiStore } = useContext(RootStoreContext);

    const columnWidths = useMemo(() => {
        const columnWidthsDesktop = [1, 1, 1, 1];
        const columnWidthsLaptopL = [0.8, 0.6, 0.8, 0.6];
        const columnWidthsLaptop = [0.6, 0.3, 0.7, 0.5];
        const columnWidthsTablet = [0.4, 0.2, 0.5, 0.3];

        return uiStore.currentScreenSize > SCREEN_SIZE.LAPTOP_L
            ? columnWidthsDesktop
            : uiStore.currentScreenSize === SCREEN_SIZE.LAPTOP_L
            ? columnWidthsLaptopL
            : uiStore.currentScreenSize === SCREEN_SIZE.LAPTOP
            ? columnWidthsLaptop
            : columnWidthsTablet;
    }, [uiStore.currentScreenSize]);

    const renderItem = useCallback(
        (attendance: IAssemblyAttendances, screenSize: ScreenSize) => {
            const isFirstTenant = attendance.isFirstTenant;

            if (screenSize === ScreenSize.MOBILE && !isFirstTenant) {
                return <React.Fragment />;
            }

            return (
                <UI.List.Item
                    key={`item-${attendance.unitname}`}
                    screenSize={screenSize}
                    rows={[
                        screenSize === ScreenSize.MOBILE ? (
                            <AttendanceListRowMobile attendances={attendance} key={`row-${attendance.unitname}`} />
                        ) : (
                            <AttendanceListRowDesktop
                                attendances={attendance}
                                key={`row-${attendance.unitname}`}
                                widths={columnWidths}
                                showTenantName={isFirstTenant}
                            />
                        )
                    ]}
                    className={isFirstTenant ? styles.ListItemWithoutLine : styles.ListItem}
                />
            );
        },
        // eslint-disable-next-line
        [columnWidths]
    );

    const renderHeader = useCallback(
        (screenSize: ScreenSize) => {
            const rows =
                screenSize === ScreenSize.MOBILE
                    ? [
                          <UI.List.Cell
                              key="tenantname"
                              colspan={1}
                              value={<Trans>screens.assemblies.attendance.tenantname</Trans>}
                              truncate
                          />,
                          <UI.List.Cell
                              key="status"
                              colspan={0.5}
                              value={<Trans>screens.assemblies.attendance.state</Trans>}
                              truncate
                          />
                      ]
                    : [
                          <UI.List.Cell
                              key="tenantname"
                              colspan={columnWidths[0]}
                              value={<Trans>screens.assemblies.attendance.tenantname</Trans>}
                              truncate
                          />,
                          <UI.List.Cell
                              key="valueQuota"
                              colspan={columnWidths[1]}
                              value={<Trans>screens.assemblies.attendance.value_quota</Trans>}
                              truncate
                          />,
                          <UI.List.Cell
                              key="unitname"
                              colspan={columnWidths[2]}
                              value={<Trans>screens.assemblies.attendance.unitname</Trans>}
                              truncate
                          />,
                          <UI.List.Cell
                              key="status"
                              colspan={columnWidths[3]}
                              value={<Trans>screens.assemblies.attendance.state</Trans>}
                              truncate
                          />
                      ];
            return <UI.List.Header rows={rows} className="p-0" />;
        },
        [columnWidths]
    );

    const handleSearchInput = useCallback(
        ({ searchQuery }) => {
            attendanceListStore.setSearchQuery(searchQuery);
        },
        [attendanceListStore]
    );

    return (
        <UI.List.BasicList
            key={attendanceListStore.assemblyAttendances.length}
            screenSize={uiStore.isMobile || props.isLiveModeSmallView ? ScreenSize.MOBILE : ScreenSize.DESKTOP}
            items={
                attendanceListStore.filteredAssemblyAttendances.length > 0 || attendanceListStore.searchQuery.length > 0
                    ? attendanceListStore.filteredAssemblyAttendances
                    : attendanceListStore.assemblyAttendances
            }
            renderItem={renderItem}
            renderHeader={renderHeader}
            itemsPerPage={attendanceListStore.itemsPerPage}
            emptyListMessage={
                attendanceListStore.assemblyAttendances.length === 0 ||
                attendanceListStore.filteredAssemblyAttendances.length === 0
                    ? t("screens.assemblies.attendance.no_attendance_found").toString()
                    : `${t("screens.assemblies.attendance.attendance_loading").toString()}...`
            }
            onSearch={handleSearchInput}
        />
    );
};
const AttendanceListRenderer = observer(AttendanceListRendererBase);

const AttendanceListBase = (props: IAttendanceListProps) => {
    const { assemblyid } = props;
    return (
        <div className={styles.AttendanceList}>
            <AttendanceDesktopTabs assemblyid={assemblyid} isLiveModeSmallView={props.isLiveModeSmallView} />
            <AttendanceQuorumInfo />
            <AttendanceListRenderer assemblyid={assemblyid} isLiveModeSmallView={props.isLiveModeSmallView} />
        </div>
    );
};

export const AttendanceList = Sentry.withProfiler(observer(AttendanceListBase));
